/**
 * @file    ProgramDetailsTab.tsx
 * @scope   VIEW
 * @summary The "Field details" tab of a program card: program picker, question picker and the
 *          advanced settings entry point.
 * @consumes RollupProgramModel, program/question option lists
 * @produces `ProgramDetailsTab` component
 * @sideEffects None — controlled inputs; changes bubble up via callbacks.
 * @notes Mirrors the Enterprise FieldDetailsTab markup/classes so both look the same.
 */
import React from 'react';
import Select from 'react-select';
import { Skeleton, SkeletonVariants } from '@cx/ui';
import { RollupProgramModel } from 'models/CrossProgramRollup/RollupMetricModel';
import { ProgramOption, QuestionOption } from './programsManagerModel';
import './ProgramDetailsTab.scss';

const customSelectStyles = {
  control: (styles: any) => ({
    ...styles,
    maxWidth: '100%',
  }),
};

interface ProgramDetailsTabProps {
  program: RollupProgramModel;
  isTouched: boolean;
  /** The selected program's questions are loading — only the question select shows a skeleton. */
  isLoading?: boolean;
  programOptions: ProgramOption[];
  questionOptions: QuestionOption[];
  onProgramSelect: (option: any) => void;
  onQuestionSelect: (option: any) => void;
  onAdvancedSettings?: () => void;
}

/** Skeleton placeholder for a single select while its options load. */
const renderSelectSkeleton = () => (
  <div className="program-details-tab__skeleton">
    <Skeleton variant={SkeletonVariants.rectangular} height="38px" />
  </div>
);

const ProgramDetailsTab: React.FC<ProgramDetailsTabProps> = ({
  program,
  isTouched,
  isLoading = false,
  programOptions,
  questionOptions,
  onProgramSelect,
  onQuestionSelect,
  onAdvancedSettings,
}) => {
  const isProgramMissing = isTouched && !program.dataCollectionGuid;
  const isQuestionMissing = isTouched && !program.questionGuid;
  const isProgramListLoading = programOptions.length === 0;

  return (
    <div>
      {isProgramListLoading ? (
        renderSelectSkeleton()
      ) : (
        <div className={`diy-input-container${isProgramMissing ? ' field-input--invalid' : ''}`}>
          <div className="diy-material-textfield">
            <Select
              className={`diy-select${isProgramMissing ? ' diy-select--invalid' : ''}`}
              options={programOptions}
              value={programOptions.find((p) => p.value === program.dataCollectionGuid) ?? null}
              onChange={onProgramSelect}
              styles={customSelectStyles}
              placeholder={isProgramMissing ? 'Program is required.' : 'Select a program...'}
            />
            <label className="diy-label">Select program</label>
          </div>
        </div>
      )}

      {isLoading ? (
        renderSelectSkeleton()
      ) : (
        <div className={`diy-input-container${isQuestionMissing ? ' field-input--invalid' : ''}`}>
          <div className="diy-material-textfield">
            <Select
              className={`diy-select${isQuestionMissing ? ' diy-select--invalid' : ''}`}
              options={questionOptions}
              value={questionOptions.find((q) => q.value === program.questionGuid) ?? null}
              onChange={onQuestionSelect}
              styles={customSelectStyles}
              isDisabled={!program.dataCollectionGuid}
              placeholder={isQuestionMissing ? 'Question is required.' : 'Select a question...'}
            />
            <label className="diy-label">Select question</label>
          </div>
        </div>
      )}
      {onAdvancedSettings && isLoading && (
        <div className="manage-fields-container">
          <Skeleton variant={SkeletonVariants.text} width="110px" />
        </div>
      )}
      {onAdvancedSettings && !isLoading && (
        <div className="manage-fields-container">
          <a
            href="#"
            className="manage-fields-link advanced-settings-link"
            onClick={(e) => {
              e.preventDefault();
              onAdvancedSettings();
            }}
          >
            Advanced settings
          </a>
        </div>
      )}
    </div>
  );
};

export default ProgramDetailsTab;
